/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {useSyncExternalStore} from "react";

/***************************************************************************
 * Cross-widget "only one row can be edited at a time" coordination.
 *
 * Each row-edit host (RecordGridWidget / RecordQuery) calls claimActiveEditor
 * when its editingRowId transitions to non-null and releaseActiveEditor when
 * it clears.  Other hosts subscribe via useOtherEditorActive; when true, they
 * render the disabled pencil + "Only one row can be edited at a time" tooltip
 * on hover — matching the in-grid behavior but spanning the whole page.
 *
 * Module-level state rather than React context because the hosts are scattered
 * across widget mounts and don't share a common ancestor provider in every
 * layout (dashboards, modals, record-screen sections).  A plain subscription
 * model lets any host plug in regardless of where it's rendered.
 ***************************************************************************/

let activeEditorId: string | null = null;
const listeners = new Set<() => void>();

function notify()
{
   listeners.forEach((fn) => fn());
}

/***************************************************************************
 * Claim the active-editor slot for this host.  Returns true when the slot
 * was free (or already owned by `id`), false when another host has it.
 *
 * Today's UI gates entry — pencils on other hosts are disabled while a
 * claim is held — but a programmatic path (deep-link, hotkey, future
 * automation) could reach this without going through the UI.  The
 * conditional claim keeps the registry honest in those cases instead of
 * silently overwriting the existing owner.
 ***************************************************************************/
export function claimActiveEditor(id: string): boolean
{
   if (activeEditorId != null && activeEditorId !== id)
   {
      return false;
   }
   activeEditorId = id;
   notify();
   return true;
}

export function releaseActiveEditor(id: string): void
{
   if (activeEditorId === id)
   {
      activeEditorId = null;
      notify();
   }
}

export function getActiveEditor(): string | null
{
   return activeEditorId;
}

/***************************************************************************
 * Returns true when some host OTHER than myId currently holds the active
 * editor claim.  useSyncExternalStore avoids unnecessary re-renders when the
 * boolean result hasn't changed (React bails out of the render when the
 * snapshot matches the previous one), which matters because claim/release
 * notify fires for every transition — including the widget that itself made
 * the transition.  Without this short-circuit the DataGrid auto-height
 * machinery was eating spurious re-renders mid-save and losing the grown-row
 * measurement, causing testEditRowGrowsWhenErrorAppears to see a 50px row
 * where the asserted grown state should have been ≥ 52px.
 ***************************************************************************/
export function useOtherEditorActive(myId: string): boolean
{
   return useSyncExternalStore(
      (onStoreChange) =>
      {
         listeners.add(onStoreChange);
         return () => listeners.delete(onStoreChange);
      },
      () => activeEditorId != null && activeEditorId !== myId,
   );
}
